import { useEffect, useRef, useState } from "react";
import { caseStudyModal } from "../constants";
import NotFound from "@/pages/NotFound";
import { gsap } from "gsap";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

interface CaseProjectProp {
  projectId: number;
  title: string;
  images: string[];
  url: string;
}

interface CaseStudyProp {
  projectId: number;
}

const SLIDE_DELAY = 4000; // 4 seconds

const CaseStudy: React.FC<CaseStudyProp> = ({ projectId }) => {
  const [project, setProject] = useState<CaseProjectProp | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Load project
  useEffect(() => {
    const foundProject = caseStudyModal.find(
      (p: CaseProjectProp) => p.projectId === projectId
    );
    setProject(foundProject ?? null);
    setActiveIndex(0);
  }, [projectId]);

  // Animate on image change
  useEffect(() => {
    if (imgRef.current) {
      gsap.fromTo(
        imgRef.current,
        { autoAlpha: 0, scale: 0.95, y: 20 },
        { autoAlpha: 1, scale: 1, y: 0, duration: 0.6, ease: "power3.out" }
      );
    }
  }, [activeIndex]);

  // Manual prev/next with looping
  const goPrev = () => {
    if (!project) return;
    const newIndex =
      activeIndex === 0 ? project.images.length - 1 : activeIndex - 1;
    setActiveIndex(newIndex);
  };

  const goNext = () => {
    if (!project) return;
    const newIndex =
      activeIndex === project.images.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(newIndex);
  };

  // Autoplay
  useEffect(() => {
    if (!project || project.images.length <= 1) return;

    if (!isPaused) {
      autoplayRef.current = setTimeout(() => {
        goNext();
      }, SLIDE_DELAY);
    }

    return () => {
      if (autoplayRef.current) clearTimeout(autoplayRef.current);
    };
  }, [activeIndex, isPaused, project]);

  if (!project) {
    return <NotFound code={204} title="Wait... What?!" />;
  }

  return (
    <div
      className="bg-transparent p-4 sm:p-6 relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      {/* Title */}
      <h1 className="text-4xl font-semibold mb-10 md:mb-4">{project.title}</h1>

      {/* Image Viewer */}
      <div className="relative flex justify-center items-center">
        {/* Prev Button */}
        {project.images.length > 1 && (
          <button
            onClick={goPrev}
            className="flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-[#f5f5f5]/80 hover:bg-[#f5f5f5] shadow-lg p-2 rounded-full transition"
          >
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
        )}

        {/* Active Image */}
        <div className="w-[90vw] h-auto md:h-[80vh] md:w-auto rounded-xl sm:rounded-2xl overflow-hidden bg-[#f5f5f5]">
          <img
            ref={imgRef}
            src={project.images[activeIndex]}
            alt={`${project.title} image ${activeIndex + 1}`}
            className="w-full h-full object-cover rounded-xl sm:rounded-2xl"
          />
        </div>

        {/* Next Button */}
        {project.images.length > 1 && (
          <button
            onClick={goNext}
            className="flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-[#f5f5f5]/80 hover:bg-[#f5f5f5] shadow-lg p-2 rounded-full transition"
          >
            <ChevronRight className="w-6 h-6 text-gray-800" />
          </button>
        )}
      </div>

      {/* Dots */}
      {project.images.length > 1 && (
        <div className="flex justify-center mt-4 space-x-2">
          {project.images.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                activeIndex === index
                  ? "bg-[#00a878] w-4 h-4"
                  : "bg-gray-400 hover:bg-gray-500"
              }`}
            />
          ))}
        </div>
      )}

      {/* Link */}
      {project.url && (
        <Link
          to={project.url}
          rel="noopener noreferrer"
          className="inline-block mt-10 md:mt-4 text-lg font-semibold text-[#0d1821] font-poppins"
        >
          View Our Services →
        </Link>
      )}
    </div>
  );
};

export default CaseStudy;
